<template>
  <div class="flex justify-center gap-3">
    <input
      v-for="idx in inputNumber"
      :key="idx"
      :ref="(el) => setInputRef(el, idx - 1)"
      :value="digits[idx - 1]"
      type="text"
      inputmode="numeric"
      autocomplete="one-time-code"
      maxlength="1"
      required
      class="w-14 h-14 text-center text-2xl font-semibold text-gray-900 bg-white border-2 border-gray-300 rounded-lg outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/30"
      @input="onInput($event, idx - 1)"
      @keydown="onKeydown($event, idx - 1)"
      @paste="onPaste($event, idx - 1)"
      @focus="($event.target as HTMLInputElement).select()"
    />
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'

interface Props {
  inputNumber: number
}

const props = defineProps<Props>()

const emit = defineEmits<{
  otpData: [value: string]
}>()

const digits = ref<string[]>(Array(props.inputNumber).fill(''))
const inputs = ref<(HTMLInputElement | null)[]>([])

const setInputRef = (el: unknown, idx: number) => {
  inputs.value[idx] = (el as HTMLInputElement) ?? null
}

const focusInput = (idx: number) => {
  const input = inputs.value[idx]
  if (input) {
    input.focus()
    input.select()
  }
}

const emitIfComplete = () => {
  if (digits.value.every((digit) => digit !== '')) {
    emit('otpData', digits.value.join(''))
  }
}

// Write digit characters into the boxes starting at `start`, then focus the
// box after the last one filled. Handles single keystrokes and pasted codes.
const fillFrom = (start: number, value: string) => {
  const chars = value.replace(/\D/g, '').split('')
  if (chars.length === 0) return start
  let idx = start
  for (const char of chars) {
    if (idx >= props.inputNumber) break
    digits.value[idx] = char
    idx++
  }
  focusInput(Math.min(idx, props.inputNumber - 1))
  emitIfComplete()
  return idx
}

const onInput = (event: Event, idx: number) => {
  const input = event.target as HTMLInputElement
  const cleaned = input.value.replace(/\D/g, '')
  if (cleaned === '') {
    digits.value[idx] = ''
    input.value = ''
    return
  }
  fillFrom(idx, cleaned)
  input.value = digits.value[idx] ?? ''
}

const onKeydown = (event: KeyboardEvent, idx: number) => {
  if (event.key === 'Backspace') {
    event.preventDefault()
    if (digits.value[idx]) {
      digits.value[idx] = ''
    } else if (idx > 0) {
      digits.value[idx - 1] = ''
      focusInput(idx - 1)
    }
  } else if (event.key === 'ArrowLeft' && idx > 0) {
    event.preventDefault()
    focusInput(idx - 1)
  } else if (event.key === 'ArrowRight' && idx < props.inputNumber - 1) {
    event.preventDefault()
    focusInput(idx + 1)
  }
}

const onPaste = (event: ClipboardEvent, idx: number) => {
  event.preventDefault()
  fillFrom(idx, event.clipboardData?.getData('text') ?? '')
}

const flush = () => {
  digits.value = Array(props.inputNumber).fill('')
  focusInput(0)
}

// Expose flush method for parent component
defineExpose({
  flush,
})
</script>
